import type { SupportingMethod } from '../types.js';

export const adxDmi: SupportingMethod = ({ candles, direction }) => {
  const period = 14;
  if (candles.length < period * 3 || direction === 'neutral') {
    return { id: 'MO-005', name: 'ADX/DMI', status: 'experimental', authority: 1, rawSignal: 0, contribution: 0, direction, finding: 'Insufficient candle history or no Core direction.', dataSufficient: false, sampleSize: candles.length };
  }
  let tr = 0;
  let plus = 0;
  let minus = 0;
  const dx: number[] = [];
  let plusSmoothed = 0;
  let minusSmoothed = 0;
  let trSmoothed = 0;
  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i]!;
    const p = candles[i - 1]!;
    const trueRange = Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close));
    const upMove = c.high - p.high;
    const downMove = p.low - c.low;
    const plusDm = upMove > downMove && upMove > 0 ? upMove : 0;
    const minusDm = downMove > upMove && downMove > 0 ? downMove : 0;
    tr += trueRange;
    plus += plusDm;
    minus += minusDm;
    if (i === period) {
      trSmoothed = tr; plusSmoothed = plus; minusSmoothed = minus;
    } else if (i > period) {
      trSmoothed = trSmoothed - trSmoothed / period + trueRange;
      plusSmoothed = plusSmoothed - plusSmoothed / period + plusDm;
      minusSmoothed = minusSmoothed - minusSmoothed / period + minusDm;
    } else continue;
    const plusDi = trSmoothed > 0 ? 100 * plusSmoothed / trSmoothed : 0;
    const minusDi = trSmoothed > 0 ? 100 * minusSmoothed / trSmoothed : 0;
    const denominator = plusDi + minusDi;
    dx.push(denominator > 0 ? 100 * Math.abs(plusDi - minusDi) / denominator : 0);
  }
  if (dx.length < period) {
    return { id: 'MO-005', name: 'ADX/DMI', status: 'experimental', authority: 1, rawSignal: 0, contribution: 0, direction, finding: 'ADX history did not reach the minimum stable sample.', dataSufficient: false, sampleSize: candles.length };
  }
  let adx = dx.slice(0, period).reduce((sum, value) => sum + value, 0) / period;
  for (let i = period; i < dx.length; i += 1) adx = (adx * (period - 1) + dx[i]!) / period;
  const latest = candles.at(-1)!;
  const previous = candles.at(-2)!;
  const upMove = latest.high - previous.high;
  const downMove = previous.low - latest.low;
  const plusDm = upMove > downMove && upMove > 0 ? upMove : 0;
  const minusDm = downMove > upMove && downMove > 0 ? downMove : 0;
  // Recompute latest smoothed DI values from the complete path for an exact terminal state.
  let trS = 0; let pS = 0; let mS = 0;
  for (let i = 1; i < candles.length; i += 1) {
    const c = candles[i]!; const p = candles[i - 1]!;
    const range = Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close));
    const up = c.high - p.high; const down = p.low - c.low;
    const pdm = up > down && up > 0 ? up : 0; const mdm = down > up && down > 0 ? down : 0;
    if (i === period) { trS = range; pS = pdm; mS = mdm; }
    else if (i > period) { trS = trS - trS / period + range; pS = pS - pS / period + pdm; mS = mS - mS / period + mdm; }
  }
  const plusDi = trS > 0 ? 100 * pS / trS : 0;
  const minusDi = trS > 0 ? 100 * mS / trS : 0;
  const aligned = adx >= 20 && ((direction === 'long' && plusDi > minusDi) || (direction === 'short' && minusDi > plusDi));
  return {
    id: 'MO-005', name: 'ADX/DMI', status: 'experimental', authority: 1, rawSignal: aligned ? 1 : 0, contribution: aligned ? 1 : 0, direction,
    finding: aligned ? `ADX ${adx.toFixed(1)} with ${direction === 'long' ? '+DI' : '-DI'} leading supports directional trend strength.` : `ADX ${adx.toFixed(1)} does not currently provide a qualifying directional trend confirmation.`,
    dataSufficient: true, sampleSize: candles.length,
  };
};
